/**
 * PR-D4-031 — QuestionCard using MadCrab_V SatTest.jsx DOM/classes verbatim.
 * PR-D4-032 — Math explanation schema adapter + [ EN | RU ] toggle + KaTeX.
 * PR-D4-062 — Universal Bluebook split layout (Math + Verbal, all modes).
 */

import { useEffect, useState } from 'react';
import type { Question, LocalizedText } from '../lib/types';
import { KaTeXRenderer } from './KaTeXRenderer';
import { DesmosViewer } from './DesmosViewer';
import { sanitizeExplanationText } from './MathQuestionCard';
import { QuestionVoteBar } from './QuestionVoteBar';
import UniversalSplitLayout, {
    PassageContent,
    getQuestionSvgFallback,
    questionHeaderLabel,
    resolveStimulusKind,
} from './UniversalSplitLayout';
import '../madcrab/SatTest.css';

interface QuestionCardProps {
    question: Question;
    questionNumber: number;
    totalQuestions: number;
    selectedOption: number | null;
    onSelectOption: (optionIndex: number) => void;
    showResult?: boolean;
    showFeedback?: boolean;
    onSubmit?: () => void;
    sectionTitle?: string;
    onExit?: () => void;
    /** Flush the PostgreSQL session cursor before leaving the exam. */
    onSaveDraft?: () => void | Promise<void>;
    className?: string;
    onNavigateToQuestion?: (index: number) => void;
    answeredQuestionIndexes?: ReadonlySet<number>;
}

const OPTION_LABELS = ['A', 'B', 'C', 'D'] as const;

export type ExplanationSource =
    | LocalizedText
    | Record<string, string>
    | string
    | null
    | undefined;

/**
 * PR-D4-032: Normalize all known explanation shapes into a single source.
 * Supports:
 *   - flat string (legacy Verbal `explanation`)
 *   - LocalizedText `{ translations: { en, ru }, default_language }`
 *   - Math 3.0 `{ en, ru }` on `explanation` or `explanations`
 *   - Verbal `explanation_ru` sibling when only EN string on `explanation`
 */
export function resolveExplanationSource(question: Question): ExplanationSource {
    const q = question as Question & {
        explanations?: Record<string, string> | LocalizedText | string | null;
        explanation_ru?: string | null;
        explanation_en?: string | null;
    };

    const primary = q.explanation as unknown;
    const plural = q.explanations as unknown;

    const primaryIsPlaceholder =
        typeof primary === 'string' &&
        primary.trim() === 'Пояснение находится в процессе генерации.';

    if (typeof primary === 'string' && primary.trim() && !primaryIsPlaceholder) {
        if (q.explanation_ru && String(q.explanation_ru).trim()) {
            return {
                en: primary,
                ru: String(q.explanation_ru),
            };
        }
        return primary;
    }
    if (primary && typeof primary === 'object') {
        return primary as ExplanationSource;
    }

    if (typeof plural === 'string' && plural.trim()) {
        return plural;
    }
    if (plural && typeof plural === 'object') {
        return plural as ExplanationSource;
    }

    if (q.explanation_ru && String(q.explanation_ru).trim()) {
        return { en: '', ru: String(q.explanation_ru) };
    }
    if (q.explanation_en && String(q.explanation_en).trim()) {
        return String(q.explanation_en);
    }

    return null;
}

function nonEmptyString(value: unknown): string {
    return typeof value === 'string' && value.trim() ? value.trim() : '';
}

/** Extract text for a language from any supported explanation source. */
export function extractExplanation(source: ExplanationSource, lang: string): string {
    if (!source) return '';
    if (typeof source === 'string') return source.trim();

    if (
        'translations' in source &&
        source.translations &&
        typeof source.translations === 'object'
    ) {
        const t = source.translations as Record<string, string>;
        const fallback = Object.values(t).find((v) => nonEmptyString(v));
        if (lang === 'ru') {
            return nonEmptyString(t.ru) || nonEmptyString(t.en) || nonEmptyString(fallback);
        }
        if (lang === 'en') {
            return nonEmptyString(t.en) || nonEmptyString(t.ru) || nonEmptyString(fallback);
        }
        return nonEmptyString(t.en) || nonEmptyString(t.ru) || nonEmptyString(fallback);
    }

    const record = source as Record<string, string>;
    const fallback = Object.values(record).find((v) => nonEmptyString(v));
    if (lang === 'ru') {
        return nonEmptyString(record.ru) || nonEmptyString(record.en) || nonEmptyString(fallback);
    }
    if (lang === 'en') {
        return nonEmptyString(record.en) || nonEmptyString(record.ru) || nonEmptyString(fallback);
    }
    return nonEmptyString(record.en) || nonEmptyString(record.ru) || nonEmptyString(fallback);
}

/** True when both EN and RU (or translations.en/ru) are present. */
export function hasBilingualExplanation(source: ExplanationSource): boolean {
    if (!source || typeof source === 'string') return false;
    if (
        'translations' in source &&
        source.translations &&
        typeof source.translations === 'object'
    ) {
        const t = source.translations as Record<string, string>;
        return Boolean(nonEmptyString(t.en) && nonEmptyString(t.ru));
    }
    const record = source as Record<string, string>;
    return Boolean(nonEmptyString(record.en) && nonEmptyString(record.ru));
}

/** True when any language has content — never show the dummy fallback then. */
export function hasAnyExplanation(source: ExplanationSource): boolean {
    return Boolean(extractExplanation(source, 'en') || extractExplanation(source, 'ru'));
}

/** Verbatim MCQOption from MadCrab_V SatTest.jsx */
function MCQOption({
    letter,
    text,
    isActive,
    isCorrect,
    isIncorrect,
    onClick,
    disabled,
}: {
    letter: string;
    text: string;
    isActive: boolean;
    isCorrect: boolean;
    isIncorrect: boolean;
    onClick: (letter: string) => void;
    disabled: boolean;
}) {
    const statusClass = isCorrect
        ? 'correct-choice'
        : isIncorrect
          ? 'incorrect-choice'
          : isActive
            ? 'active-choice'
            : '';
    return (
        <button
            type="button"
            className={`mcq-option-item ${statusClass}`}
            onClick={() => onClick(letter)}
            disabled={disabled}
        >
            <span className="choice-letter">{letter}</span>
            <div className="choice-text">
                <KaTeXRenderer text={text} />
            </div>
        </button>
    );
}

export function QuestionCard({
    question,
    questionNumber,
    totalQuestions,
    selectedOption,
    onSelectOption,
    showResult = false,
    showFeedback = false,
    onSubmit,
    sectionTitle = 'Reading and Writing',
    onExit,
    onSaveDraft,
    onNavigateToQuestion,
    answeredQuestionIndexes,
}: QuestionCardProps) {
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        setLocalSubmitted(false);
    }, [question.id, questionNumber]);

    const feedbackVisible = showResult || (showFeedback && localSubmitted);
    const passage = (question.passage ?? '').trim();
    const svgFallback = getQuestionSvgFallback(question);
    const stimulusKind = resolveStimulusKind(question);

    const explanationSource = resolveExplanationSource(question);
    const bilingual = hasBilingualExplanation(explanationSource);
    const anyExplanation = hasAnyExplanation(explanationSource);

    const [explanationLang, setExplanationLang] = useState<'en' | 'ru'>('en');

    useEffect(() => {
        const src = resolveExplanationSource(question);
        setExplanationLang(hasBilingualExplanation(src) ? 'ru' : 'en');
    }, [question.id]);

    const correctLetter = OPTION_LABELS[question.correct_option_index] ?? 'A';
    const rawExplanation = extractExplanation(explanationSource, explanationLang);
    const explanationText = sanitizeExplanationText(rawExplanation, correctLetter);

    const letterFromIndex = (idx: number) => OPTION_LABELS[idx] ?? 'A';
    const selectedLetter =
        selectedOption != null ? letterFromIndex(selectedOption) : null;

    const handleLetterClick = (letter: string) => {
        if (feedbackVisible) return;
        const idx = OPTION_LABELS.indexOf(letter as (typeof OPTION_LABELS)[number]);
        if (idx >= 0) onSelectOption(idx);
    };

    const handleSubmit = () => {
        if (showFeedback && !localSubmitted) {
            setLocalSubmitted(true);
            return;
        }
        onSubmit?.();
    };

    const stimulus =
        stimulusKind === 'diagram' ? (
            <>
                <div className="universal-split__diagram-frame">
                    <div className="sat-passage__graphic w-full h-full overflow-hidden">
                        <DesmosViewer
                            graphData={question.graph_data}
                            svgFallback={svgFallback}
                            height={460}
                            width="100%"
                        />
                    </div>
                </div>
                {passage ? <PassageContent text={passage} /> : null}
            </>
        ) : stimulusKind === 'passage' ? (
            <PassageContent text={passage} />
        ) : null;

    return (
        <div className="trainer-mode-layout">
            <header className="sat-topbar flex justify-between items-center px-3 md:px-10 border-b border-slate-700 bg-[#1e293b] text-white z-50 h-[64px] md:h-[72px] relative">
                <div className="sat-topbar__start relative z-20 flex items-center gap-2 shrink-0">
                    {onExit ? (
                        <button
                            type="button"
                            className="sat-topbar-badge sat-topbar__save-exit"
                            onClick={() => {
                                void Promise.resolve(onSaveDraft?.()).finally(() => {
                                    onExit();
                                });
                            }}
                            title="Save & Exit"
                            aria-label="Save & Exit"
                        >
                            <span className="sat-topbar__save-exit-label md:inline hidden">Save & Exit</span>
                            <span className="sat-topbar__save-exit-label md:hidden inline">Save</span>
                        </button>
                    ) : null}
                </div>

                <h1 className="sat-topbar__title pointer-events-none relative z-[1] font-bold text-sm md:text-lg text-white m-0 truncate max-w-[120px] md:max-w-none">
                    {sectionTitle}
                </h1>

                <div className="sat-topbar__meta flex items-center gap-2 md:gap-3">
                    <div className="flex items-center bg-slate-800 rounded-lg p-1">
                        <div className="sat-topbar-badge sat-topbar-badge--timer tabular-nums">
                            {questionNumber}/{totalQuestions}
                        </div>
                    </div>
                </div>
            </header>

            <main className="trainer-fluid-container flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
                {onNavigateToQuestion ? (
                    <aside
                        className="question-navigator order-first w-full shrink-0 border-b border-slate-200 bg-slate-50 p-3 md:order-none md:w-20 md:border-b-0 md:border-r md:px-2 md:py-5"
                        aria-label="Question navigator"
                    >
                        <div className="flex gap-2 overflow-x-auto md:flex-col md:items-center md:overflow-y-auto">
                            {Array.from({ length: totalQuestions }, (_, index) => {
                                const isCurrent = index === questionNumber - 1;
                                const isAnswered = answeredQuestionIndexes?.has(index) ?? false;
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => onNavigateToQuestion(index)}
                                        className={`q-bubble shrink-0 ${isCurrent ? 'current' : ''} ${isAnswered ? 'filled' : ''}`}
                                        aria-label={`Go to question ${index + 1}`}
                                    >
                                        {index + 1}
                                    </button>
                                );
                            })}
                        </div>
                    </aside>
                ) : null}

                <section className="trainer-content-area w-full flex-1 min-h-0 overflow-y-auto md:overflow-hidden relative">
                    <UniversalSplitLayout
                        mode="exam"
                        stimulusKind={stimulusKind}
                        header={
                            <h2 className="universal-split__header-title mt-0">
                                {questionHeaderLabel(questionNumber, totalQuestions)}
                            </h2>
                        }
                        stimulus={stimulus}
                    >
                        <div className="question-text mt-0">
                            <KaTeXRenderer text={question.question_text} />
                        </div>

                        <div className="answer-interaction-box w-full">
                            <div className="mcq-options-list">
                                {question.options.map((text, idx) => {
                                    const letter = letterFromIndex(idx);
                                    const isSelected = selectedLetter === letter;
                                    const isCorrect =
                                        feedbackVisible && idx === question.correct_option_index;
                                    const isIncorrect =
                                        feedbackVisible &&
                                        isSelected &&
                                        idx !== question.correct_option_index;
                                    return (
                                        <MCQOption
                                            key={letter}
                                            letter={letter}
                                            text={text}
                                            isActive={isSelected}
                                            isCorrect={isCorrect}
                                            isIncorrect={isIncorrect}
                                            onClick={handleLetterClick}
                                            disabled={feedbackVisible}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {feedbackVisible ? (
                            <div className="sat-feedback-box w-full">
                                <div
                                    className="sat-feedback-title"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                    }}
                                >
                                    <span>Разбор</span>
                                    {bilingual ? (
                                        <span
                                            aria-label="Explanation language"
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 12,
                                                letterSpacing: '0.02em',
                                                userSelect: 'none',
                                            }}
                                        >
                                            [{' '}
                                            <button
                                                type="button"
                                                onClick={() => setExplanationLang('en')}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    font: 'inherit',
                                                    fontWeight: explanationLang === 'en' ? 800 : 500,
                                                    color:
                                                        explanationLang === 'en'
                                                            ? '#0f172a'
                                                            : '#64748b',
                                                }}
                                            >
                                                EN
                                            </button>
                                            {' | '}
                                            <button
                                                type="button"
                                                onClick={() => setExplanationLang('ru')}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    font: 'inherit',
                                                    fontWeight: explanationLang === 'ru' ? 800 : 500,
                                                    color:
                                                        explanationLang === 'ru'
                                                            ? '#0f172a'
                                                            : '#64748b',
                                                }}
                                            >
                                                RU
                                            </button>
                                            {' ]'}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="sat-feedback-text">
                                    {anyExplanation && explanationText ? (
                                        <div className="math-text math-text--prose">
                                            <KaTeXRenderer text={explanationText} />
                                        </div>
                                    ) : (
                                        <div style={{ fontStyle: 'italic', color: '#666' }}>
                                            Explanation is unavailable for this question.
                                        </div>
                                    )}
                                </div>
                                <QuestionVoteBar questionId={question.id} />
                            </div>
                        ) : null}

                        {!feedbackVisible && onSubmit && selectedOption !== null ? (
                            <div>
                                <button
                                    type="button"
                                    className="step-btn highlight"
                                    onClick={handleSubmit}
                                >
                                    Submit Answer
                                </button>
                            </div>
                        ) : null}
                    </UniversalSplitLayout>
                </section>
            </main>
        </div>
    );
}

export default QuestionCard;
